import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Truck, MapPin, Radio, RefreshCw, Users, Navigation, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { cityCoords } from '../components/TrackingMap';

const truckIcon = (color = '#00A651') => L.divIcon({
  className: 'fleet-truck',
  html: `<div style="background:${color};color:white;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(0,0,0,.3);border:3px solid white;"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 18H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v11"/><path d="M14 9h4l4 4v5c0 .6-.4 1-1 1h-2"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

function Recenter({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 8);
    else if (points.length > 1) map.fitBounds(points, { padding: [50, 50] });
  }, [points, map]);
  return null;
}

// Sample demo trucks (when no real GPS data yet)
const demoFleet = [
  { load_id: 'LD9001', driver_name: 'Sohan Lal', lat: 21.1458, lng: 79.0882, origin: 'Mumbai', destination: 'Delhi', material: 'Textiles', vehicle_number: 'MH 12 AB 3456', speed: 62 },
  { load_id: 'LD9002', driver_name: 'Ramesh N', lat: 12.9716, lng: 77.5946, origin: 'Bengaluru', destination: 'Chennai', material: 'Electronics', vehicle_number: 'KA 05 CD 7890', speed: 0 },
  { load_id: 'LD9003', driver_name: 'Bhavesh P', lat: 22.3072, lng: 73.1812, origin: 'Ahmedabad', destination: 'Pune', material: 'Cement', vehicle_number: 'GJ 01 EF 1234', speed: 55 },
  { load_id: 'LD9004', driver_name: 'Praveen K', lat: 17.3850, lng: 78.4867, origin: 'Hyderabad', destination: 'Kolkata', material: 'Pharma', vehicle_number: 'TS 09 GH 5678', speed: 68 },
  { load_id: 'LD9005', driver_name: 'Karan Singh', lat: 26.9124, lng: 75.7873, origin: 'Delhi', destination: 'Jaipur', material: 'FMCG', vehicle_number: 'DL 03 IJ 9876', speed: 72 },
  { load_id: 'LD9006', driver_name: 'Anil Yadav', lat: 25.3176, lng: 82.9739, origin: 'Varanasi', destination: 'Kolkata', material: 'Food Grains', vehicle_number: 'UP 65 KL 4321', speed: 45 },
  { load_id: 'LD9007', driver_name: 'Farhan A', lat: 15.3647, lng: 75.1240, origin: 'Hubli', destination: 'Mumbai', material: 'Fruits (Reefer)', vehicle_number: 'KA 25 MN 6543', speed: 58 },
  { load_id: 'LD9008', driver_name: 'Manoj K', lat: 26.1445, lng: 91.7362, origin: 'Guwahati', destination: 'Delhi', material: 'Tea', vehicle_number: 'AS 01 OP 8765', speed: 40 },
];

export default function FleetMap() {
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/fleet/map').then(r => {
      const real = (r.data || []).filter(t => t.lat != null && t.lng != null);
      if (real.length > 0) setFleet([...real, ...demoFleet.slice(0, Math.max(0, 6 - real.length))]);
      else setFleet(demoFleet);
    }).catch(() => setFleet(demoFleet)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const points = fleet.map(t => [t.lat, t.lng]);
  const active = fleet.filter(t => (t.speed || 0) > 5).length;
  const idle = fleet.length - active;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">Fleet Live Map</h1>
          <p className="text-sm text-gray-500 mt-1">Every truck across India, in real-time.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-[#00A651]/10 text-[#00A651] border-0"><Radio className="w-3 h-3 mr-1 animate-pulse" />LIVE</Badge>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><Users className="w-3.5 h-3.5" />Total Fleet</div>
          <p className="text-2xl font-extrabold text-[#0B2545] font-[Poppins]">{fleet.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><Navigation className="w-3.5 h-3.5 text-[#00A651]" />Moving</div>
          <p className="text-2xl font-extrabold text-[#00A651] font-[Poppins]">{active}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><Clock className="w-3.5 h-3.5 text-[#FFB800]" />Idle</div>
          <p className="text-2xl font-extrabold text-[#B37800] font-[Poppins]">{idle}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><MapPin className="w-3.5 h-3.5" />Cities Covered</div>
          <p className="text-2xl font-extrabold text-[#0B2545] font-[Poppins]">{new Set(fleet.map(f => (f.origin || '').split(',')[0])).size}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden h-[520px]">
          <MapContainer center={[22.5, 79]} zoom={5} className="w-full h-full" scrollWheelZoom={true} style={{ zIndex: 1 }}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {fleet.map((t, i) => (
              <Marker key={i} position={[t.lat, t.lng]} icon={truckIcon((t.speed || 0) > 5 ? '#00A651' : '#B37800')} eventHandlers={{ click: () => setSelected(t) }}>
                <Popup>
                  <div className="text-xs">
                    <p className="font-bold text-[#0B2545]">{t.driver_name}</p>
                    <p className="font-mono">{t.vehicle_number}</p>
                    <p>{t.origin} → {t.destination}</p>
                    <p>Speed: {t.speed || 0} km/h</p>
                  </div>
                </Popup>
              </Marker>
            ))}
            {points.length > 0 && <Recenter points={points} />}
          </MapContainer>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 h-[520px] overflow-y-auto">
          <p className="text-sm font-bold text-[#0B2545] mb-3">All Vehicles ({fleet.length})</p>
          <div className="space-y-2">
            {fleet.map((t, i) => (
              <div key={i} onClick={() => setSelected(t)} className={`border rounded-lg p-3 cursor-pointer transition-colors ${selected?.load_id === t.load_id ? 'border-[#00A651] bg-[#00A651]/5' : 'border-gray-100 hover:border-gray-200'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[10px] text-gray-500">{t.load_id || '—'}</span>
                  <Badge className={`border-0 text-[10px] ${(t.speed || 0) > 5 ? 'bg-[#00A651]/10 text-[#00A651]' : 'bg-[#FFB800]/15 text-[#B37800]'}`}>
                    {(t.speed || 0) > 5 ? `${Math.round(t.speed)} km/h` : 'Idle'}
                  </Badge>
                </div>
                <p className="font-semibold text-sm text-[#0B2545]">{t.driver_name}</p>
                <p className="text-[10px] text-gray-500 font-mono">{t.vehicle_number}</p>
                <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-[#00A651]" />
                  {t.origin?.split(',')[0]} → {t.destination?.split(',')[0]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500 text-center">Real driver GPS is shown when drivers share live location from the Driver App. Demo trucks shown as fallback for the map view.</p>
    </div>
  );
}
